import _ from 'lodash';
import Immutable from 'seamless-immutable';

import RefData from 'ref-data';
import { logger, CapabilityGate, capabilities } from '../../common/Services';
import { sortBy } from '../../common/collections';
import mechanicTypes from './mechanic-types';

/**
 * Workspace
 * @typedef {Object} Workspace
 * @property {string} classRef - experience property
 * @property {string} entrySourceStrategies - how can users enter the experience?
 * @property {string} formatType - experience property
 * @property {boolean} isInNetwork - is the current experience an in network one
 * @property {boolean} isMicro - is the current experience a microexperience
 * @property {boolean} isNewContentService - does the experience support the new content service
 * @property {boolean} isPromotionCampaign - is the current experience a promotion one
 * @property {boolean} isSocialCampaign - is the current experience a social one
 * @property {string[]} publishFormatTypeIds - ids for specifying supported publishing format types
 * @property {Object[]} publishFormatTypes - array of publish formats
 * @property {string} subtypeRef - experience property
 * @property {string} typeRef - experience property
 * @property {boolean} canUseForms - If the user can update and use forms
 * @property {boolean} canPublish - does the user have permission to publish content
 * @property {boolean} canDeleteExperience - can the user delete an experience
 * @property {boolean} hasExperienceAdminActions - does the user have permissions for any admin actions
 * @property {boolean} canEditExperience - does the user have permission to edit an experience
 * @property {boolean} canExportContent - does the user have permission to export content
 * @property {boolean} canViewEntries - does the user have permission to view entries
 */

const defaultWorkspace = {
  mechanics:  [],
  formatType: {},
  subtypeRef: {},
};
function create(workspace = defaultWorkspace) {
  return Immutable(workspace, { prototype: model });
}

function mergeExperience(workspace, experience) {
  return workspace.merge(_.pick(experience, _experienceKeys));
}

function decorateWorkspaceWithUser(workspace, user) {
  return workspace.set('user', user);
}

const getFraudDetectionStrategy = _.memoize(
  (fraudDetectionStrategies, captchaCapabilities) =>
    _.filter(fraudDetectionStrategies, (strategy) => captchaCapabilities[strategy.id]),
  (fraudDetectionStrategies, captchaCapabilities) =>
    `${JSON.stringify(fraudDetectionStrategies)}-${JSON.stringify(captchaCapabilities)}`
);

//object defining disabled fraud detection strategies
const disabledStrategies = {
  [RefData.Enum.STRATEGY.FRAUD.DYNAMICCAPTCHA]: true,
  [RefData.Enum.STRATEGY.FRAUD.RECAPTCHA]:      true,
};

const getFraudDetectionStrategies = () => {
  const captchaCapabilities = {
    [RefData.Enum.STRATEGY.FRAUD.NONE]:                    true, // this option is not dependent on capability and always available for the user
    [RefData.Enum.STRATEGY.FRAUD.TURNSTILE]:               CapabilityGate(capabilities.CLOUDFLARE_TURNSTILE_CAPTCHA),
    [RefData.Enum.STRATEGY.FRAUD.DYNAMICCAPTCHA]:          CapabilityGate(capabilities.ALWAYS_GOOGLE_CAPTCHA),
    [RefData.Enum.STRATEGY.FRAUD.RECAPTCHA]:               CapabilityGate(capabilities.SMART_GOOGLE_CAPTCHA),
    [RefData.Enum.STRATEGY.FRAUD.DYNAMICCAPTCHATURNSTILE]: CapabilityGate(capabilities.CLOUDFLARE_TURNSTILE_CAPTCHA),
  };

  const fraudStrategies = getFraudDetectionStrategy(
    RefData.Collection.hydrateAll(RefData.Enum.STRATEGY.FRAUD),
    captchaCapabilities
  );
  return sortBy(
    (s) => s.order,
    _.map(fraudStrategies, (strategy) => ({ ...strategy, isDisabled: disabledStrategies[strategy.id] }))
  );
};

const model = {
  get isInNetwork() {
    return (
      this.subtypeRef &&
      _.includes(
        [
          RefData.Enum.SUBTYPE.CAMPAIGNOBJECT.SWEEPSTAKETWITTER,
          RefData.Enum.SUBTYPE.CAMPAIGNOBJECT.SWEEPSTAKEINSTAGRAM,
          RefData.Enum.SUBTYPE.CAMPAIGNOBJECT.SWEEPSTAKEMULTINETWORKNOFORM,
          RefData.Enum.SUBTYPE.CAMPAIGNOBJECT.SWEEPSTAKEMULTINETWORKENTRYFORM,
          RefData.Enum.SUBTYPE.CAMPAIGNOBJECT.SWEEPSTAKEMULTINETWORKCLAIMFORM,
        ],
        this.subtypeRef.id
      )
    );
  },
  get isMicro() {
    return this.formatType.id === RefData.Enum.FORMAT.DISPLAY.MICRO;
  },

  get isNewContentService() {
    return this.appVersion >= 70;
  },
  get publishFormatTypes() {
    return RefData.Collection.hydrateAll(this.publishFormatTypeIds);
  },
  get entrySourceStrategies() {
    return RefData.Collection.hydrateAll(this.mechanics[mechanicTypes.ENTRIES].entrySourceStrategyIds);
  },
  get entryFrequencyStrategies() {
    return RefData.Collection.hydrateAll(RefData.Enum.STRATEGY.ENTRY);
  },
  get fraudDetectionStrategies() {
    return getFraudDetectionStrategies();
  },
  get votingOptions() {
    return sortBy((s) => s.order, RefData.Collection.hydrateAll(RefData.Enum.STRATEGY.VOTING));
  },
  get judgingOptions() {
    return sortBy((s) => s.order, RefData.Collection.hydrateAll(RefData.Enum.STRATEGY.JUDGING));
  },
  get announcementOptions() {
    return sortBy((s) => s.order, RefData.Collection.hydrateAll(RefData.Enum.STRATEGY.ANNOUNCEMENT));
  },

  canUseMechanic(mechanicType) {
    switch (mechanicType) {
      case mechanicTypes.ENTRIES:
        return this.mechanics[mechanicTypes.ENTRIES].isSupported;
      case mechanicTypes.INSIGHTS:
        return this.mechanics[mechanicTypes.INSIGHTS].isSupported;
      case mechanicTypes.REWARDS:
        return this.mechanics[mechanicTypes.REWARDS].isSupported;
      case mechanicTypes.PROMOTIONS:
        return this.mechanics[mechanicTypes.PROMOTIONS].isSupported;
      default:
        logger.warn(`WorkspaceModel:canUseMechanic - Unknown mechanicType: ${mechanicTypes}`);
    }
  },
  get canUseForms() {
    return (
      _.includes(
        [
          RefData.Enum.TYPE.CAMPAIGNOBJECT.SWEEPSTAKE,
          RefData.Enum.TYPE.CAMPAIGNOBJECT.QUIZ,
          RefData.Enum.TYPE.CAMPAIGNOBJECT.SIGNUP,
        ],
        this.typeRef.id
      ) && _.get(this, 'user.permissions.isAdmin', false)
    );
  },
  get isPublisher() {
    return _.get(this, 'user.permissions.isPublisher', false);
  },
  get canPublish() {
    return this.isPublisher && this.status.id !== RefData.Enum.STATUS.CAMPAIGNOBJECT.TEMPLATE;
  },
  get canDeleteExperience() {
    return _.get(this, 'user.permissions.canDeleteExperience', false);
  },
  get hasExperienceAdminActions() {
    const adminActions = {
      canDeleteExperience: _.get(this, 'user.permissions.canDeleteExperience', false),
    };
    return _.keys(_.filter(adminActions, (value, key) => value)).length > 0;
  },
  get canEditExperience() {
    return _.get(this, 'user.permissions.isCreator', false);
  },
  get canExportContent() {
    return _.get(this, 'user.permissions.canExportContent', false);
  },
  get canExportWinners() {
    return this.canExportContent || this.canAdministerWinner;
  },
  get canViewEntries() {
    return _.get(this, 'user.permissions.canViewEntries', false);
  },
  get canEditEntriesSettings() {
    return _.get(this, 'user.permissions.isSupport', false) || this.canExportContent || this.canViewEntries;
  },
  get canAdministerWinner() {
    return _.get(this, 'user.permissions.canAdministerWinner', false);
  },
  get canManageAccount() {
    return _.get(this, 'user.permissions.isManager', false);
  },
  get canEditMetadata() {
    return !_.isEmpty(_.get(this, 'experienceMetadata.fieldsByVisibility.edit'), false);
  },
};

const _experienceKeys = [
  'appVersion',
  'classRef',
  'formatType',
  'subtypeRef',
  'typeRef',
  'hasRules',
  'experienceMetadata',
  'status',
  'isTemplate',
  'canBeRepublished',
];

export default {
  create,
  mergeExperience,
  decorateWorkspaceWithUser,
};
